import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Spin, Tooltip, message } from 'antd';
import { DownloadOutlined, ExpandOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { downloadFile } from '../Utils/HelperFunctions';

const ZOOM_STEP = 1.25;
const MAX_SCALE = 4; // relative to the image's natural size

// Zoomable image viewer. Wheel zoom is anchored on the cursor; the zoom buttons
// are anchored on the centre of the visible area.
function ImagePreviewModal({ visible, onClose, src, fileName }) {
  const [viewport, setViewport] = useState(null);
  const [natural, setNatural] = useState(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [downloading, setDownloading] = useState(false);
  const drag = useRef(null);

  const fitScale = viewport && natural ? Math.min(viewport.clientWidth / natural.w, viewport.clientHeight / natural.h) : 1;

  const fit = useCallback(() => {
    if (!viewport || !natural) return;
    const scale = Math.min(viewport.clientWidth / natural.w, viewport.clientHeight / natural.h);
    setView({
      scale,
      x: (viewport.clientWidth - natural.w * scale) / 2,
      y: (viewport.clientHeight - natural.h * scale) / 2
    });
  }, [viewport, natural]);

  useEffect(fit, [fit]);

  const zoomBy = useCallback(
    (factor, cx = viewport?.clientWidth / 2, cy = viewport?.clientHeight / 2) => {
      if (!viewport) return;
      setView(v => {
        const scale = Math.min(Math.max(v.scale * factor, fitScale), MAX_SCALE);
        const ratio = scale / v.scale;
        return { scale, x: cx - (cx - v.x) * ratio, y: cy - (cy - v.y) * ratio };
      });
    },
    [viewport, fitScale]
  );

  // React's onWheel is passive, so preventDefault needs a native listener
  useEffect(() => {
    if (!viewport) return;
    const onWheel = e => {
      e.preventDefault();
      const rect = viewport.getBoundingClientRect();
      zoomBy(Math.exp(-e.deltaY * 0.002), e.clientX - rect.left, e.clientY - rect.top);
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [viewport, zoomBy]);

  const onPointerDown = e => {
    drag.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = e => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    setView(v => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const onDownload = () => {
    setDownloading(true);
    fetch(src)
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.blob();
      })
      .then(blob => downloadFile(blob, fileName))
      .catch(() => message.error('There was some problem downloading the image'))
      .finally(() => setDownloading(false));
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width='95vw'
      centered
      destroyOnClose
      afterClose={() => setNatural(null)}
      className='image-preview-modal'
      title={
        <div className='image-preview-toolbar'>
          <Tooltip title='Zoom out'>
            <Button icon={<ZoomOutOutlined />} onClick={() => zoomBy(1 / ZOOM_STEP)} disabled={!natural} />
          </Tooltip>
          <span className='image-preview-zoom'>{natural ? Math.round(view.scale * 100) : 0}%</span>
          <Tooltip title='Zoom in'>
            <Button icon={<ZoomInOutlined />} onClick={() => zoomBy(ZOOM_STEP)} disabled={!natural} />
          </Tooltip>
          <Tooltip title='Fit to screen'>
            <Button icon={<ExpandOutlined />} onClick={fit} disabled={!natural} />
          </Tooltip>
          <Button type='primary' icon={<DownloadOutlined />} loading={downloading} onClick={onDownload}>
            Download Original
          </Button>
        </div>
      }>
      <div
        ref={setViewport}
        className='image-preview-viewport'
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}>
        {!natural && <Spin size='large' className='image-preview-spinner' />}
        <img
          src={src}
          alt='preview'
          draggable={false}
          onLoad={e => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
          style={{
            visibility: natural ? 'visible' : 'hidden',
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`
          }}
        />
      </div>
    </Modal>
  );
}

export default ImagePreviewModal;
